import { useNode } from '../contexts/NodeContext';
import AlertBoardPage from './AlertBoardPage';

/**
 * All Sites ▸ Alert Board — the cross-site board, next to the Live Wall in the same scope.
 *
 * TARGET: one page showing the alert hexagon for EVERY site at once.
 * CURRENT: the hexagon for the selected node only — the same view as
 * a site's own Alert Board. The route and nav entry exist now so the
 * multi-site version can land behind them without another nav change; the
 * banner keeps that limitation visible instead of the page quietly looking
 * like it already aggregates.
 *
 * LAYOUT: `flex h-full flex-col` is load-bearing, not decoration. AlertHexagonPage's
 * root is `flex h-full flex-col` and its canvas is `lg:flex-1` inside that, so it
 * needs a parent with a DEFINITE height. Wrapping it in a plain auto-height <div>
 * (as this page first did) collapses the canvas to zero and the hexagon vanishes.
 * Layout's <main> is a flex item with a resolved height, so `h-full` here has
 * something real to resolve against — then `min-h-0 flex-1` hands the remainder,
 * minus the banner, down to the hexagon.
 */
export default function MultiSiteAlertPreviewPage() {
  const { selectedNode } = useNode();

  return (
    <div className="flex h-full flex-col">
      <div className="mx-3 mt-3 sm:mx-6 sm:mt-6 flex-none rounded-lg border border-border bg-card px-4 py-3">
        <p className="text-[13px] font-medium text-foreground">Multi-Site Alert Board</p>
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          {selectedNode
            ? `Showing ${selectedNode.name} only — the combined all-sites hexagon is not built yet.`
            : 'Select a site from the title bar to preview its alerts. The combined all-sites hexagon is not built yet.'}
        </p>
      </div>

      <div className="min-h-0 flex-1">
        {/* Pinned to the honeycomb, toggle hidden: this page is about alerts across sites,
            and the board's toggle navigates — it would take the operator straight off here
            onto the per-node board. */}
        <AlertBoardPage fixedView="hex" />
      </div>
    </div>
  );
}
